import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";

// v6.5.0 Portfolio Heatmap
// Family (rows) x Symbol (cols) grid coloured by risk-adjusted score.
// Users can toggle between colouring by SCORE (growth/dd), CHAMPIONS
// (progress toward 10-per-cell goal), or GROWTH (raw avg growth %).

const MODES = [
  { key: "score",     label: "Risk-Adj Score", desc: "avg growth / max DD · champion bonus" },
  { key: "champions", label: "Champion Progress", desc: "champions vs 10-per-cell target" },
  { key: "growth",    label: "Avg Growth %", desc: "average growth per cell" },
];

function pickHeat(mode, cell, maxScore, maxGrowth) {
  if (cell.total === 0) return { bg: "transparent", border: "#2A2D3A" };
  if (mode === "champions") {
    const p = cell.progress || 0;   // 0..1
    if (p >= 1)   return { bg: "#D6FF38", border: "#D6FF38" };
    if (p >= 0.5) return { bg: "rgba(214,255,56,0.55)", border: "#D6FF38" };
    if (p >= 0.2) return { bg: "rgba(214,255,56,0.25)", border: "rgba(214,255,56,0.4)" };
    if (cell.elite > 0) return { bg: "rgba(96,165,250,0.2)", border: "#60A5FA" };
    return { bg: "rgba(148,163,184,0.08)", border: "#475569" };
  }
  if (mode === "growth") {
    const g = cell.avg_growth;
    if (g >= 40) return { bg: "#2ECA7F", border: "#2ECA7F" };
    if (g >= 15) return { bg: "rgba(46,202,127,0.55)", border: "#2ECA7F" };
    if (g >= 0)  return { bg: "rgba(46,202,127,0.2)", border: "#2ECA7F" };
    if (g >= -15) return { bg: "rgba(255,77,77,0.25)", border: "#FF4D4D" };
    return { bg: "#FF4D4D", border: "#FF4D4D" };
  }
  // default: score
  const s = cell.score;
  const norm = maxScore > 0 ? Math.min(1, s / maxScore) : 0;
  if (norm >= 0.75) return { bg: "#D6FF38",  border: "#D6FF38" };
  if (norm >= 0.4)  return { bg: "rgba(214,255,56,0.55)", border: "#D6FF38" };
  if (norm >= 0.1)  return { bg: "rgba(46,202,127,0.35)", border: "#2ECA7F" };
  if (norm >= 0)    return { bg: "rgba(148,163,184,0.1)", border: "#475569" };
  return { bg: "rgba(255,77,77,0.4)", border: "#FF4D4D" };
}

function CellTooltip({ cell }) {
  return (
    <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block pointer-events-none">
      <div className="border border-[#2A2D3A] bg-[#0F1015] rounded-md p-3 text-[11px] mono whitespace-nowrap shadow-xl">
        <div className="text-[#F1F5F9] font-medium mb-2">
          {cell.family} <span className="text-[#64748B]">on</span> {cell.symbol}
        </div>
        <div className="space-y-0.5 text-[#94A3B8]">
          <div>Champions <span className="text-[#D6FF38]">{cell.champions}</span> / 10</div>
          <div>Elite <span className="text-[#60A5FA]">{cell.elite}</span></div>
          <div>Basic <span className="text-[#94A3B8]">{cell.basic}</span></div>
          <div>Total <span className="text-[#F1F5F9]">{cell.total}</span></div>
          <div className="pt-1 border-t border-[#2A2D3A] mt-1">
            avg growth <span className={cell.avg_growth >= 0 ? "text-[#2ECA7F]" : "text-[#FF4D4D]"}>{cell.avg_growth}%</span>
          </div>
          <div>avg DD <span className="text-[#FF4D4D]">{cell.avg_dd}%</span></div>
          <div>score <span className="text-[#F1F5F9]">{cell.score}</span></div>
        </div>
      </div>
    </div>
  );
}

export default function Heatmap() {
  const [data, setData]   = useState(null);
  const [mode, setMode]   = useState("score");
  const [group, setGroup] = useState(true);

  useEffect(() => {
    api.get(`/portfolio-heatmap?group_by_base=${group}`)
      .then((r) => setData(r.data))
      .catch(console.error);
  }, [group]);

  if (!data) {
    return <div className="p-6 mono text-sm text-[#64748B]" data-testid="heatmap-loading">loading portfolio grid...</div>;
  }

  const maxScore = data.cells.flat().reduce((m, c) => Math.max(m, c.score || 0), 0);
  const maxGrowth = data.cells.flat().reduce((m, c) => Math.max(m, c.avg_growth || 0), 0);

  return (
    <div data-testid="portfolio-heatmap-page" className="p-6 space-y-5">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] mono uppercase tracking-widest text-[#64748B]">Portfolio</div>
          <h1 className="text-2xl tracking-tight font-medium mt-1">Fleet Heatmap</h1>
          <div className="text-[11px] mono text-[#64748B] mt-1">
            {data.total_bots} bots · {data.total_champions} champions ·
            target {data.target}/cell · {data.families.length} families x {data.symbols.length} symbols
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex bg-[#0F1015] border border-[#2A2D3A] rounded-md overflow-hidden">
            {MODES.map((m) => (
              <button
                key={m.key}
                onClick={() => setMode(m.key)}
                className={`px-3 py-1.5 mono text-[10px] uppercase tracking-widest transition-colors ${
                  mode === m.key ? "bg-[#D6FF38] text-black" : "text-[#94A3B8] hover:text-[#F1F5F9]"
                }`}
                data-testid={`mode-${m.key}`}
              >
                {m.label}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-2 mono text-[10px] uppercase tracking-widest text-[#94A3B8]">
            <input
              type="checkbox"
              checked={group}
              onChange={(e) => setGroup(e.target.checked)}
              className="accent-[#D6FF38]"
              data-testid="heatmap-group-toggle"
            />
            group SB variants
          </label>
        </div>
      </div>

      <div className="text-[11px] mono text-[#64748B]">
        {MODES.find((m) => m.key === mode)?.desc}
      </div>

      {/* Heatmap grid */}
      <div className="overflow-x-auto border border-[#2A2D3A] bg-[#15171E] rounded-md">
        <table className="border-collapse min-w-full">
          <thead>
            <tr>
              <th className="sticky left-0 bg-[#15171E] text-[10px] mono uppercase tracking-widest text-[#64748B] text-right pr-3 py-2 border-b border-[#2A2D3A]">
                family \ symbol
              </th>
              {data.symbols.map((s) => (
                <th key={s.symbol}
                    className="text-[10px] mono uppercase tracking-widest text-[#94A3B8] px-2 py-2 border-b border-[#2A2D3A] whitespace-nowrap"
                    data-testid={`hm-col-${s.symbol}`}>
                  <div>{s.symbol}</div>
                  <div className="text-[9px] text-[#475569]">{s.champions} champs</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.cells.map((row, ri) => (
              <tr key={data.families[ri].code} data-testid={`hm-row-${data.families[ri].code}`}>
                <td className="sticky left-0 bg-[#15171E] text-[10px] mono uppercase tracking-widest text-[#F1F5F9] text-right pr-3 py-2 border-r border-[#2A2D3A] whitespace-nowrap">
                  <div>{data.families[ri].label || data.families[ri].code}</div>
                  <div className="text-[9px] text-[#475569]">{data.families[ri].champions} champs</div>
                </td>
                {row.map((cell) => {
                  const heat = pickHeat(mode, cell, maxScore, maxGrowth);
                  const primary =
                    mode === "champions" ? cell.champions :
                    mode === "growth"    ? (cell.avg_growth >= 0 ? "+" + cell.avg_growth : cell.avg_growth) :
                                            cell.score;
                  const showText = cell.total > 0;
                  return (
                    <td key={cell.symbol + cell.family}
                        className="relative group p-1 border-r border-b border-[#2A2D3A]"
                        data-testid={`hm-cell-${cell.family}-${cell.symbol}`}>
                      <div
                        className="w-14 h-10 flex flex-col items-center justify-center rounded-sm transition-transform hover:scale-110 cursor-help"
                        style={{ background: heat.bg, border: `1px solid ${heat.border}` }}
                      >
                        <div className="mono text-[11px] font-medium"
                             style={{ color: heat.bg === "transparent" ? "#475569" : "#0B0C10" }}>
                          {showText ? primary : "-"}
                        </div>
                        {showText && (
                          <div className="mono text-[8px] uppercase"
                               style={{ color: heat.bg === "transparent" ? "#64748B" : "rgba(11,12,16,0.7)" }}>
                            n={cell.total}
                          </div>
                        )}
                      </div>
                      {showText && <CellTooltip cell={cell} />}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-6 text-[10px] mono uppercase tracking-widest text-[#64748B] flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-sm" style={{ background: "#D6FF38" }} />
          <span>saturated / goal met</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-sm" style={{ background: "rgba(214,255,56,0.55)" }} />
          <span>on track</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-sm" style={{ background: "rgba(148,163,184,0.15)", border: "1px solid #475569" }} />
          <span>early / thin</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-sm" style={{ background: "#FF4D4D" }} />
          <span>graveyarding</span>
        </div>
      </div>
    </div>
  );
}
